import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import PhotoZoom from "../../../components/PhotoZoom/PhotoZoom";
import VideoZoom from "../../../components/VideoZoom/VideoZoom";
import { AnasayfaService } from "../../../services/anasayfa.service";
import { AnasayfaItem } from "../../../services/types";

const validationSchema = Yup.object().shape({
  baslik: Yup.string()
    .required("Başlık alanı zorunludur")
    .max(100, "Başlık en fazla 100 karakter olmalıdır"),
  url: Yup.string().optional(),
  sira: Yup.number()
    .required("Sıra alanı zorunludur")
    .min(1, "Sıra 1'den küçük olamaz"),
});

const AnasayfaYonetimi: React.FC = () => {
  const [items, setItems] = useState<AnasayfaItem[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState<AnasayfaItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [photoZoomVisible, setPhotoZoomVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [videoZoomVisible, setVideoZoomVisible] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const toast = useRef<Toast>(null);

  const [formData, setFormData] = useState({
    baslik: "",
    url: "",
    sira: 0,
    dosyaEkle: null as File | null,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await AnasayfaService.gorselListele();
      if (response.data) {
        setItems(response.data);
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Veriler yüklenirken bir hata oluştu",
      });
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });

      if (!formData.dosyaEkle && !editMode) {
        toast.current?.show({
          severity: "error",
          summary: "Hata",
          detail: "Lütfen bir görsel seçiniz",
        });
        return;
      }

      if (editMode && selectedItem) {
        const updateData = {
          id: selectedItem.id,
          baslik: formData.baslik,
          url: formData.url || "",
          sira: formData.sira,
          dosya: selectedItem.dosya,
          dosyaTip: selectedItem.dosyaTip,
          ...(formData.dosyaEkle && { dosyaEkle: formData.dosyaEkle }),
        };
        await AnasayfaService.gorselGuncelle(updateData);
        toast.current?.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Görsel başarıyla güncellendi",
        });
      } else if (formData.dosyaEkle) {
        await AnasayfaService.gorselEkle({
          ...formData,
          dosyaEkle: formData.dosyaEkle,
        });
        toast.current?.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Görsel başarıyla eklendi",
        });
      }
      hideDialog();
      loadData();
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach((err) => {
          toast.current?.show({
            severity: "error",
            summary: "Doğrulama Hatası",
            detail: err.message,
          });
        });
        return;
      }
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "İşlem sırasında bir hata oluştu",
      });
    }
  };

  const confirmDelete = (item: AnasayfaItem) => {
    confirmDialog({
      message: "Bu görseli silmek istediğinizden emin misiniz?",
      header: "Silme Onayı",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Evet",
      rejectLabel: "Hayır",
      accept: () => handleDelete(item.id),
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await AnasayfaService.gorselSil(id);
      toast.current?.show({
        severity: "success",
        summary: "Başarılı",
        detail: "Görsel başarıyla silindi",
      });
      loadData();
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Silme işlemi sırasında bir hata oluştu",
      });
    }
  };

  const showDialog = (item?: AnasayfaItem) => {
    if (item) {
      setEditMode(true);
      setSelectedItem(item);
      setFormData({
        baslik: item.baslik,
        url: item.url || "",
        sira: item.sira,
        dosyaEkle: null,
      });
    } else {
      setEditMode(false);
      setSelectedItem(null);
      setFormData({
        baslik: "",
        url: "",
        sira: 0,
        dosyaEkle: null,
      });
    }
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
    setEditMode(false);
    setSelectedItem(null);
    setFormData({
      baslik: "",
      url: "",
      sira: 0,
      dosyaEkle: null,
    });
  };

  const actionBodyTemplate = (rowData: AnasayfaItem) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-pencil"
          rounded
          outlined
          className="mr-2"
          onClick={() => showDialog(rowData)}
        />
        <Button
          icon="pi pi-trash"
          rounded
          outlined
          severity="danger"
          onClick={() => confirmDelete(rowData)}
        />
      </div>
    );
  };

  const imageBodyTemplate = (rowData: AnasayfaItem) => {
    if (
      rowData.dosyaTip === ".png" ||
      rowData.dosyaTip === ".jpg" ||
      rowData.dosyaTip === ".jpeg" ||
      rowData.dosyaTip === ".gif" ||
      rowData.dosyaTip === ".webp"
    ) {
      return (
        <img
          src={rowData.dosya}
          alt={rowData.baslik}
          onClick={() => handleImageClick(rowData.dosya)}
          className="w-8rem shadow-2 border-round cursor-pointer "
        />
      );
    } else if (
      rowData.dosyaTip === ".mp4" ||
      rowData.dosyaTip === ".avi" ||
      rowData.dosyaTip === ".mov" ||
      rowData.dosyaTip === ".wmv" ||
      rowData.dosyaTip === ".flv" ||
      rowData.dosyaTip === ".webm"
    ) {
      return (
        <video
          src={rowData.dosya}
          onClick={() => handleVideoClick(rowData.dosya)}
          className="w-8rem shadow-2 border-round cursor-pointer "
        />
      );
    }
    return null;
  };

  const handleVideoClick = (videoUrl: string) => {
    setSelectedVideo(videoUrl);
    setVideoZoomVisible(true);
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setPhotoZoomVisible(true);
  };

  return (
    <div className="card p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex justify-content-between align-items-center mb-4">
        <h1 className="text-xl">Anasayfa Görselleri</h1>
        <Button
          label="Yeni Görsel Ekle"
          icon="pi pi-plus"
          onClick={() => showDialog()}
        />
      </div>

      <DataTable
        value={items}
        paginator
        rows={10}
        dataKey="id"
        loading={loading}
        emptyMessage="Görsel bulunamadı"
        sortField="sira"
        sortOrder={1}
      >
        <Column field="sira" header="Sıra" sortable style={{ width: "10%" }} />
        <Column
          header="Görsel"
          body={imageBodyTemplate}
          style={{ width: "20%" }}
        />
        <Column
          field="baslik"
          header="Başlık"
          sortable
          style={{ width: "30%" }}
        />
        <Column
          field="url"
          header="Yönlendirme URL'si"
          sortable
          style={{ width: "30%" }}
        />
        <Column body={actionBodyTemplate} style={{ width: "10%" }} />
      </DataTable>

      <Dialog
        visible={dialogVisible}
        header={editMode ? "Görsel Düzenle" : "Yeni Görsel Ekle"}
        baseZIndex={100}
        modal
        className="p-fluid w-full md:w-6"
        onHide={hideDialog}
      >
        <div className="field mt-4">
          <label htmlFor="baslik">Başlık</label>
          <InputText
            id="baslik"
            value={formData.baslik}
            onChange={(e) =>
              setFormData({ ...formData, baslik: e.target.value })
            }
          />
        </div>

        <div className="field">
          <label htmlFor="url">Yönlendirme URL'si</label>
          <InputText
            id="url"
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          />
        </div>

        <div className="field">
          <label htmlFor="sira">Sıra</label>
          <InputNumber
            id="sira"
            value={formData.sira}
            onValueChange={(e) =>
              setFormData({ ...formData, sira: e.value || 1 })
            }
          />
        </div>

        <div className="field">
          <label htmlFor="dosya">Görsel</label>
          <FileUpload
            mode="basic"
            name="dosya"
            accept="image/*,application/pdf,image/gif,image/jpeg,image/png,video/*"
            maxFileSize={8000000} // 8 MB
            chooseLabel={editMode ? "Görseli Değiştir" : "Görsel Seç"}
            onSelect={(e) =>
              setFormData({
                ...formData,
                dosyaEkle: e.files[0],
              })
            }
          />
        </div>

        <div className="flex justify-content-end mt-4">
          <Button
            label="İptal"
            icon="pi pi-times"
            outlined
            onClick={hideDialog}
            className="mr-2"
          />
          <Button label="Kaydet" icon="pi pi-check" onClick={handleSubmit} />
        </div>
      </Dialog>

      {photoZoomVisible && selectedImage && (
        <PhotoZoom
          src={selectedImage}
          onClose={() => {
            setPhotoZoomVisible(false);
            setSelectedImage(null);
          }}
        />
      )}
      {videoZoomVisible && selectedVideo && (
        <VideoZoom
          src={selectedVideo}
          onClose={() => {
            setVideoZoomVisible(false);
            setSelectedVideo(null);
          }}
        />
      )}
    </div>
  );
};

export default AnasayfaYonetimi;
